import { useCallback, useState } from 'react';

/** Shared props so typed-confirm fields are not mistaken for OTP / verification-code inputs. */
export const typedConfirmPhraseInputProps = {
  type: 'text',
  name: 'typed-confirmation-phrase',
  autoComplete: 'off',
  autoCorrect: 'off',
  autoCapitalize: 'off',
  spellCheck: false,
  inputMode: 'text',
  'data-1p-ignore': 'true',
  'data-lpignore': 'true',
  'data-form-type': 'other',
};

/**
 * Desktop browsers (especially Safari) may still suggest OTP codes when nearby copy
 * mentions 2FA/MFA, so on a fine pointer we start the field readOnly until the first
 * interaction to block that autofill dropdown. On a coarse pointer (touch phone/tablet,
 * including iOS PWAs) a readOnly+autoFocus field never opens the keyboard, so we skip the
 * initial readOnly there entirely — the tradeoff is safe because touch platforms don't show
 * the same inline OTP-suggestion chip Safari desktop does.
 */
export function shouldStartTypedConfirmAutofillBlock() {
  try {
    if (typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches) {
      return false;
    }
  } catch {
    return true;
  }
  return true;
}

export function useTypedConfirmPhraseInput() {
  const [blockAutofill, setBlockAutofill] = useState(() => shouldStartTypedConfirmAutofillBlock());
  const releaseAutofillBlock = useCallback(() => {
    setBlockAutofill(false);
  }, []);

  // Consumers must bind all three: onTouchStart is required so the block clears
  // BEFORE focus fires on iOS (onFocus/onMouseDown alone are too late there).
  const typedConfirmReleaseHandlers = {
    onFocus: releaseAutofillBlock,
    onMouseDown: releaseAutofillBlock,
    onTouchStart: releaseAutofillBlock,
  };

  return { blockAutofill, releaseAutofillBlock, typedConfirmReleaseHandlers };
}
